/**
 * Brain — KS2-I2 Governed Retrieval
 *
 * Evidence hierarchy enforced at display time:
 *  1. CANON_LOCKED passages (primary governed substrate)
 *  2. CANDIDATE_ANALYTICAL claims/constraints (accepted for evidence review — NOT canon)
 *  Unreviewed machine-extracted candidates and synthetic fixtures are excluded
 *  from query results (enforced by brain_engine.py).
 *
 * Brain displays read-only retrieval results.
 * The Brain UI exposes no controls to mutate canon, claims, or constraints.
 * Governed read-only behavior is enforced in the backend (test suite: 131 tests).
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';

// ── Visual constants ─────────────────────────────────────────────────────────

const FINDING_TYPE_STYLE = {
  'Changed':        'bg-gray-100 text-gray-700 border border-gray-200',
  'Conflict':       'bg-black text-white border border-black',
  'Stale':          'bg-gray-100 text-gray-600 border border-gray-200',
  'Missing source': 'bg-gray-50 text-gray-500 border border-gray-200',
};

const REVIEW_STATE_STYLE = {
  Pending:   'text-gray-500',
  Approved:  'text-emerald-600 font-medium',
  Rejected:  'text-gray-400',
  Held:      'text-gray-400',
  Escalated: 'text-gray-700 font-medium',
};

const EVIDENCE_DOT = {
  supported:   '#03f37c',
  conflicting: '#000000',
  uncertain:   '#737373',
  missing:     '#d4d4d4',
};

const EVIDENCE_STATUS_COLORS = {
  CANON_LOCKED:         'bg-emerald-50 text-emerald-800 border border-emerald-200',
  CANDIDATE_ANALYTICAL: 'bg-amber-50 text-amber-800 border border-amber-200',
};

// ── PF3 Live Intelligence constants ───────────────────────────────────────────

const VERDICT_CONFIG = {
  GROUNDED_ANSWER: {
    badgeClass: 'bg-emerald-900 text-emerald-100',
    label: 'GROUNDED ANSWER',
    description: 'Answer directly supported by cited institutional evidence.',
    borderClass: 'border-emerald-300',
  },
  ABSTAIN: {
    badgeClass: 'bg-black text-white',
    label: 'ABSTAIN — HUMAN REVIEW REQUIRED',
    description: 'Insufficient or conflicting evidence. A Canon Owner must intervene.',
    borderClass: 'border-black',
  },
  CONFLICT: {
    badgeClass: 'bg-amber-800 text-amber-100',
    label: 'CONFLICT DETECTED',
    description: 'Evidence sources disagree. No resolution is possible without governance action.',
    borderClass: 'border-amber-400',
  },
  INSUFFICIENT_EVIDENCE: {
    badgeClass: 'bg-gray-700 text-gray-100',
    label: 'INSUFFICIENT EVIDENCE',
    description: 'No applicable governed passage matched this query in the corpus.',
    borderClass: 'border-gray-300',
  },
};

const PROVIDER_STATUS_STYLE = {
  LIVE:                   'text-emerald-700 bg-emerald-50 border-emerald-200',
  DETERMINISTIC_FALLBACK: 'text-amber-700 bg-amber-50 border-amber-200',
  DETERMINISTIC:          'text-gray-600 bg-gray-50 border-gray-200',
  UNAVAILABLE:            'text-red-700 bg-red-50 border-red-200',
  DISABLED:               'text-gray-500 bg-gray-100 border-gray-200',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function ResultCard({ result, idx }) {
  const statusColor = EVIDENCE_STATUS_COLORS[result.evidence_status] || 'bg-gray-50 text-gray-700 border border-gray-200';

  return (
    <div
      data-testid={`query-result-${idx}`}
      className="border border-gray-200 rounded-lg p-4 space-y-2"
    >
      <div className="flex flex-wrap items-start gap-2">
        <span
          className={`text-xs px-2 py-0.5 rounded font-mono tracking-wide ${statusColor}`}
          data-testid="result-evidence-status"
        >
          {result.evidence_status}
        </span>
        <span className="text-xs text-gray-400 font-mono">{result.anchor}</span>
      </div>

      {/* evidence_label — explicit disclosure of status, not implied canon */}
      <p
        className="text-xs text-gray-500 italic"
        data-testid="result-evidence-label"
      >
        {result.evidence_label}
      </p>

      <p className="text-sm text-gray-800 leading-relaxed" data-testid="result-text">
        {result.text}
      </p>

      <div className="text-xs text-gray-500 space-y-0.5">
        <div>
          Source: <span className="text-gray-700 font-medium">{result.source_title}</span>
          {result.version_number && (
            <span className="ml-2 text-gray-500">({result.version_number})</span>
          )}
        </div>
        <div data-testid="result-provenance">
          Provenance: <span className="font-mono text-gray-500">{result.provenance || result.source_version_id}</span>
        </div>
      </div>
    </div>
  );
}

function AbstainBanner({ reason }) {
  return (
    <div
      data-testid="abstain-banner"
      className="border-2 border-black rounded-lg p-5 space-y-2 bg-gray-50"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono font-bold px-2 py-0.5 rounded bg-black text-white tracking-widest">
          ABSTAIN
        </span>
        <span className="text-xs font-mono text-gray-600 uppercase tracking-wide">
          Human Review Required
        </span>
      </div>
      <p
        className="text-sm text-gray-800 leading-relaxed"
        data-testid="abstain-reason"
      >
        {reason}
      </p>
      <p className="text-xs text-gray-500">
        The Brain cannot select one side of an unresolved conflict. A Canon Owner must
        determine which record takes precedence before a governed answer can be issued.
      </p>
    </div>
  );
}

function EvidenceHierarchyNote({ note }) {
  if (!note) return null;
  return (
    <div
      data-testid="evidence-hierarchy-note"
      className="text-xs text-gray-500 border border-gray-200 rounded p-3 bg-gray-50"
    >
      <strong className="text-gray-700">Evidence hierarchy: </strong>
      {note}
    </div>
  );
}

// ── Governed Query tab ────────────────────────────────────────────────────────

function GovernedQueryTab() {
  const [queryText, setQueryText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [queryError, setQueryError] = useState('');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get('/brain/queries');
      setHistory(r.data.queries || []);
    } catch {
      // history is telemetry only — non-fatal
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!queryText.trim()) return;
    setSubmitting(true);
    setQueryError('');
    setResult(null);
    try {
      const r = await api.post('/brain/query', { query_text: queryText.trim() });
      setResult(r.data);
    } catch (err) {
      setQueryError(err.response?.data?.detail || 'Query failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleHistoryToggle() {
    if (!showHistory) loadHistory();
    setShowHistory(v => !v);
  }

  return (
    <div className="space-y-5" data-testid="governed-query-tab">
      {/* Scope disclosure */}
      <div className="text-xs text-gray-500 border border-gray-200 rounded p-3">
        <strong className="text-gray-700">Governed retrieval scope: </strong>
        Queries search canon-locked passages and accepted candidate analytical records only.
        Unreviewed machine-extracted candidates and synthetic fixture data are excluded.
        Results are deterministic — no live AI model is invoked.
      </div>

      {/* Query form */}
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="brain-query-form">
        <textarea
          data-testid="brain-query-input"
          value={queryText}
          onChange={e => setQueryText(e.target.value)}
          placeholder="Enter governed query…"
          rows={3}
          className="w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:border-gray-500"
        />
        <button
          type="submit"
          data-testid="brain-query-submit"
          disabled={submitting || !queryText.trim()}
          className="px-4 py-2 text-sm font-medium rounded bg-black text-white disabled:opacity-50 hover:opacity-80 transition-opacity"
        >
          {submitting ? 'Querying…' : 'Submit governed query'}
        </button>
      </form>

      {queryError && (
        <p className="text-sm text-red-600 border border-red-200 rounded p-3" data-testid="brain-query-error">
          {queryError}
        </p>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4" data-testid="brain-query-result">
          {/* ABSTAIN */}
          {result.abstain && result.abstain_reason && (
            <AbstainBanner reason={result.abstain_reason} />
          )}

          {/* CANON_ANSWER — only shown when not abstaining */}
          {!result.abstain && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-gray-900 text-gray-100 tracking-wide">
                CANON_ANSWER
              </span>
              <span className="text-xs text-gray-500">
                {result.result_count} result{result.result_count !== 1 ? 's' : ''} returned
              </span>
            </div>
          )}

          <EvidenceHierarchyNote note={result.evidence_hierarchy_note} />

          {result.results && result.results.length > 0 ? (
            <div className="space-y-3">
              {result.results.map((r, i) => (
                <ResultCard key={r.passage_id || r.claim_id || r.constraint_id || i} result={r} idx={i} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No matching governed records found for this query.</p>
          )}

          {result.tenant_note && (
            <p className="text-xs text-gray-400 italic" data-testid="tenant-note">
              {result.tenant_note}
            </p>
          )}
        </div>
      )}

      {/* Query history (telemetry) */}
      <div className="border-t border-gray-100 pt-4">
        <button
          data-testid="history-toggle"
          onClick={handleHistoryToggle}
          className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
        >
          {showHistory ? 'Hide' : 'Show'} query history ({history.length > 0 ? history.length : '…'})
        </button>

        {showHistory && (
          <div className="mt-3 space-y-2" data-testid="query-history-list">
            {historyLoading && (
              <p className="text-xs text-gray-400">Loading history…</p>
            )}
            {!historyLoading && history.length === 0 && (
              <p className="text-xs text-gray-400">No prior queries recorded.</p>
            )}
            {!historyLoading && history.map((q) => (
              <div
                key={q.id}
                data-testid={`history-entry-${q.id}`}
                className="flex items-center justify-between gap-3 text-xs border border-gray-100 rounded p-2"
              >
                <span className="text-gray-600 truncate">{q.query_text}</span>
                <div className="flex items-center gap-2 shrink-0">
                  {q.abstain && (
                    <span className="font-mono px-1.5 py-0.5 rounded bg-black text-white text-xs">
                      ABSTAIN
                    </span>
                  )}
                  <span className="text-gray-400">{q.result_count} result{q.result_count !== 1 ? 's' : ''}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Findings tab (KS1 legacy — MockBrainProvider) ────────────────────────────

function FindingsTab() {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/brain/findings')
      .then(r => setFindings(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="font-body text-gray-400 py-4">Loading findings…</p>;
  if (error)   return <p className="font-body text-gray-700 py-4">Error: {error}</p>;

  return (
    <div data-testid="findings-tab">
      {findings.length === 0 ? (
        <p className="font-body text-gray-400">No findings at this time.</p>
      ) : (
        <div className="space-y-3" data-testid="findings-list">
          {findings.map((f) => (
            <Link
              key={f.id}
              to={`/app/brain/${f.id}`}
              className="block border border-gray-200 rounded p-5 hover:border-gray-400 transition-colors"
              data-testid={`finding-row-${f.id}`}
            >
              <div className="flex flex-wrap items-start gap-2 mb-2">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-heading font-medium ${FINDING_TYPE_STYLE[f.finding_type] || 'bg-gray-100 text-gray-600'}`}
                >
                  {f.finding_type}
                </span>
                {f.abstain && (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-heading font-bold border border-black bg-white text-black"
                    data-testid={`abstain-badge-${f.id}`}
                  >
                    ABSTAIN
                  </span>
                )}
                <span className={`ml-auto text-xs font-heading ${REVIEW_STATE_STYLE[f.review_state] || 'text-gray-500'}`}>
                  {f.review_state}
                </span>
              </div>
              <h3 className="font-heading font-semibold text-black mb-1" style={{ fontSize: '15px' }}>
                {f.title}
              </h3>
              <p className="font-body text-gray-500 text-sm leading-relaxed line-clamp-2">
                {f.summary}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                <span className="font-body text-xs text-gray-400">{f.source_ref}</span>
                <span className="inline-flex items-center gap-1.5 font-body text-xs text-gray-500">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: EVIDENCE_DOT[f.evidence_state] || '#a3a3a3' }}
                  />
                  {f.evidence_state}
                </span>
                <span className="font-body text-xs text-gray-400">{f.assigned_authority}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ── PF3 Live Intelligence sub-components ─────────────────────────────────────

function ProviderStatusBadge({ status }) {
  if (!status) return null;
  const cls = PROVIDER_STATUS_STYLE[status.status] || PROVIDER_STATUS_STYLE.DETERMINISTIC;
  return (
    <div
      data-testid="provider-status-badge"
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-mono ${cls}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {status.status}
      {status.model_id && status.model_id !== 'deterministic' && (
        <span className="opacity-60 ml-1">· {status.model_id}</span>
      )}
      {status.fallback_used && (
        <span className="ml-1 px-1 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">
          FALLBACK
        </span>
      )}
    </div>
  );
}

function VerdictBanner({ verdict, abstainReason, conflictState, answer, reasoningSummary }) {
  const cfg = VERDICT_CONFIG[verdict] || VERDICT_CONFIG.ABSTAIN;
  return (
    <div
      data-testid="verdict-banner"
      className={`border-2 ${cfg.borderClass} rounded-lg p-4 space-y-3`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          data-testid="verdict-badge"
          className={`text-xs font-mono font-bold px-2.5 py-1 rounded tracking-widest ${cfg.badgeClass}`}
        >
          {cfg.label}
        </span>
      </div>
      <p className="text-xs text-gray-500 italic">{cfg.description}</p>

      {answer && (
        <div className="pt-1 space-y-1">
          <p className="text-xs font-mono text-gray-400 uppercase tracking-wide">Answer</p>
          <p
            data-testid="governed-answer"
            className="text-sm text-gray-800 leading-relaxed whitespace-pre-line"
          >
            {answer}
          </p>
        </div>
      )}

      {reasoningSummary && (
        <div className="pt-1 space-y-1">
          <p className="text-xs font-mono text-gray-400 uppercase tracking-wide">Reasoning summary</p>
          <p
            data-testid="reasoning-summary"
            className="text-sm text-gray-700 leading-relaxed italic"
          >
            {reasoningSummary}
          </p>
          <p className="text-xs text-gray-400">
            ANALYTICAL INFERENCE — reasoning above the direct evidence. Not institutional authority.
          </p>
        </div>
      )}

      {(abstainReason || conflictState) && (
        <div className="pt-1 space-y-1">
          {conflictState && (
            <p
              data-testid="conflict-state"
              className="text-xs font-mono text-amber-700"
            >
              CONFLICT STATE: {conflictState}
            </p>
          )}
          {abstainReason && (
            <p
              data-testid="abstain-reason"
              className="text-sm text-gray-700 leading-relaxed"
            >
              {abstainReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function CitationCard({ citation, idx, contextItems }) {
  // Look up governance state from context_items for this passage
  const ctxItem = (contextItems || []).find(c => c.passage_id === citation.passage_id);
  const govState = ctxItem?.governance_state;
  const isCandidate = ctxItem?.is_candidate;
  const govClass = govState === 'CANON_LOCKED'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';

  return (
    <div
      data-testid={`citation-${idx}`}
      className="border border-gray-200 rounded-lg p-3 space-y-2"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-mono text-gray-400">
          [{idx + 1}]
        </span>
        {govState && (
          <span
            data-testid={`citation-gov-state-${idx}`}
            className={`text-xs px-1.5 py-0.5 rounded border font-mono ${govClass}`}
          >
            {govState}
          </span>
        )}
        {isCandidate && (
          <span className="text-xs text-amber-700 italic">
            CANDIDATE_ANALYTICAL — not established canon
          </span>
        )}
        <span className="text-xs text-gray-400 font-mono ml-auto">
          {citation.anchor}
        </span>
      </div>

      {citation.quote && (
        <blockquote
          data-testid={`citation-quote-${idx}`}
          className="text-sm text-gray-700 border-l-2 border-gray-300 pl-3 italic leading-relaxed"
        >
          "{citation.quote}"
        </blockquote>
      )}

      <div className="text-xs text-gray-500 space-y-0.5">
        <div>
          Source: <span className="text-gray-700 font-medium">{citation.source_title || '—'}</span>
        </div>
        <div className="font-mono">
          passage_id: <span className="text-gray-600">{citation.passage_id}</span>
        </div>
      </div>
    </div>
  );
}

function ContextSummaryPanel({ contextItems, contextFingerprint, contextTruncated }) {
  const [expanded, setExpanded] = useState(false);
  if (!contextItems || contextItems.length === 0) return null;
  const canonCount = contextItems.filter(i => i.governance_state === 'CANON_LOCKED').length;
  const candidateCount = contextItems.filter(i => i.is_candidate).length;

  return (
    <div
      data-testid="context-summary-panel"
      className="border border-gray-200 rounded-lg p-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-mono text-gray-500 uppercase tracking-wide">Context packet supplied to model</p>
        <button
          data-testid="context-expand-toggle"
          onClick={() => setExpanded(v => !v)}
          className="text-xs text-gray-400 hover:text-gray-700 transition-colors"
        >
          {expanded ? 'collapse' : 'expand'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-gray-600">
        <span>{contextItems.length} passage{contextItems.length !== 1 ? 's' : ''} supplied</span>
        <span className="text-emerald-700">{canonCount} CANON_LOCKED</span>
        {candidateCount > 0 && (
          <span className="text-amber-700">{candidateCount} CANDIDATE_ANALYTICAL</span>
        )}
        {contextTruncated && (
          <span className="text-gray-400 italic">corpus truncated at budget limit</span>
        )}
      </div>

      <div className="font-mono text-xs text-gray-400 truncate">
        fingerprint: {contextFingerprint?.slice(0, 32)}…
      </div>

      {expanded && (
        <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
          {contextItems.map((item, i) => (
            <div
              key={item.passage_id}
              data-testid={`context-item-${i}`}
              className="flex items-center gap-2 text-xs text-gray-600 py-0.5"
            >
              <span className={`px-1.5 py-0.5 rounded text-xs font-mono border ${
                item.governance_state === 'CANON_LOCKED'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-amber-50 text-amber-700 border-amber-200'
              }`}>
                {item.governance_state === 'CANON_LOCKED' ? 'CANON' : 'CAND.'}
              </span>
              <span className="font-mono text-gray-400">{item.passage_id}</span>
              <span className="text-gray-500 truncate">{item.source_title}</span>
              <span className="text-gray-400 ml-auto shrink-0">{item.anchor}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GovernedQueryHistoryPanel({ history, loading, show, onToggle }) {
  const VERDICT_BADGE = {
    GROUNDED_ANSWER:      'bg-emerald-900 text-emerald-100',
    ABSTAIN:              'bg-black text-white',
    CONFLICT:             'bg-amber-800 text-amber-100',
    INSUFFICIENT_EVIDENCE:'bg-gray-600 text-gray-100',
  };

  return (
    <div className="border-t border-gray-100 pt-4" data-testid="governed-query-history">
      <button
        data-testid="governed-history-toggle"
        onClick={onToggle}
        className="text-xs text-gray-500 hover:text-gray-800 transition-colors"
      >
        {show ? 'Hide' : 'Show'} governed query history
        {history.length > 0 && ` (${history.length})`}
      </button>

      {show && (
        <div className="mt-3 space-y-2" data-testid="governed-history-list">
          {loading && <p className="text-xs text-gray-400">Loading history…</p>}
          {!loading && history.length === 0 && (
            <p className="text-xs text-gray-400">No governed queries recorded yet.</p>
          )}
          {!loading && history.map((q) => (
            <div
              key={q.query_id || q.id}
              data-testid={`governed-history-entry-${q.query_id || q.id}`}
              className="flex items-center gap-2 text-xs border border-gray-100 rounded p-2"
            >
              <span className="text-gray-600 truncate flex-1">{q.query_text}</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <span
                  className={`font-mono px-1.5 py-0.5 rounded text-xs ${
                    VERDICT_BADGE[q.verdict] || 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {q.verdict}
                </span>
                {q.fallback_used && (
                  <span className="text-amber-600 text-xs">FALLBACK</span>
                )}
                {q.latency_ms != null && (
                  <span className="text-gray-400">{q.latency_ms}ms</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LiveGovernedTab() {
  const [queryText, setQueryText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [queryError, setQueryError] = useState('');
  const [providerStatus, setProviderStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    api.get('/brain/provider-status')
      .then(r => setProviderStatus(r.data))
      .catch(() => {});
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get('/brain/governed-queries');
      setHistory(r.data.queries || []);
    } catch {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!queryText.trim()) return;
    setSubmitting(true);
    setQueryError('');
    setResult(null);
    try {
      const r = await api.post('/brain/governed-query', { query_text: queryText.trim() });
      setResult(r.data);
      // Refresh provider status and history after a query
      api.get('/brain/provider-status').then(s => setProviderStatus(s.data)).catch(() => {});
      if (showHistory) loadHistory();
    } catch (err) {
      setQueryError(err.response?.data?.detail || 'Governed query failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function handleHistoryToggle() {
    if (!showHistory) loadHistory();
    setShowHistory(v => !v);
  }

  return (
    <div className="space-y-5" data-testid="live-governed-tab">
      {/* Constitutional rule disclosure */}
      <div className="text-xs text-gray-500 border border-gray-200 rounded p-3 space-y-1">
        <p>
          <strong className="text-gray-700">Constitutional rule: </strong>
          The model may reason over institutional truth. It may not create institutional truth.
        </p>
        <p>
          Results are READ-ONLY. No canon, evidence, lifecycle state, or governance record
          is modified by a Brain query. Answers are analytical — they do not constitute
          institutional authority.
        </p>
      </div>

      {/* Provider status */}
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500 font-mono">Provider:</span>
        <ProviderStatusBadge status={providerStatus} />
        {providerStatus?.live_available === false && (
          <span className="text-xs text-gray-400 italic">
            {providerStatus?.status === 'DISABLED'
              ? 'Live provider is disabled. Deterministic mode active.'
              : 'Live provider unavailable. Deterministic fallback active.'}
          </span>
        )}
      </div>

      {/* Query form */}
      <form onSubmit={handleSubmit} className="space-y-3" data-testid="live-governed-query-form">
        <textarea
          data-testid="live-query-input"
          value={queryText}
          onChange={e => setQueryText(e.target.value)}
          placeholder="Enter governed query… (max 2000 characters)"
          rows={3}
          maxLength={2000}
          className="w-full border border-gray-200 rounded px-3 py-2 text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:border-gray-500"
        />
        <div className="flex items-center gap-3">
          <button
            type="submit"
            data-testid="live-query-submit"
            disabled={submitting || !queryText.trim()}
            className="px-4 py-2 text-sm font-medium rounded bg-black text-white disabled:opacity-50 hover:opacity-80 transition-opacity"
          >
            {submitting ? 'Querying…' : 'Submit governed query'}
          </button>
          {queryText.length > 1800 && (
            <span className="text-xs text-amber-600">
              {2000 - queryText.length} characters remaining
            </span>
          )}
        </div>
      </form>

      {queryError && (
        <p
          className="text-sm text-red-600 border border-red-200 rounded p-3"
          data-testid="live-query-error"
        >
          {queryError}
        </p>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4" data-testid="live-query-result">
          {/* Provider & telemetry metadata */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="font-mono">
              provider: <span className="text-gray-700">{result.provider}</span>
            </span>
            {result.model_id && result.model_id !== 'deterministic' && (
              <span className="font-mono">
                model: <span className="text-gray-700">{result.model_id}</span>
              </span>
            )}
            <span className="font-mono">
              latency: <span className="text-gray-700">{result.latency_ms}ms</span>
            </span>
            {result.fallback_used && (
              <span
                data-testid="fallback-indicator"
                className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
              >
                DETERMINISTIC FALLBACK — {result.fallback_reason || 'provider unavailable'}
              </span>
            )}
            {result.citation_valid_count > 0 && (
              <span className="text-emerald-600">
                {result.citation_valid_count} valid citation{result.citation_valid_count !== 1 ? 's' : ''}
              </span>
            )}
            {result.citation_rejected_count > 0 && (
              <span className="text-red-600">
                {result.citation_rejected_count} rejected citation{result.citation_rejected_count !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {/* Main verdict + answer */}
          <VerdictBanner
            verdict={result.verdict}
            abstainReason={result.abstain_reason}
            conflictState={result.conflict_state}
            answer={result.answer}
            reasoningSummary={result.reasoning_summary}
          />

          {/* Evidence state */}
          {result.evidence_state && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-400 font-mono">Evidence state:</span>
              <span
                data-testid="evidence-state-badge"
                className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200 font-mono"
              >
                {result.evidence_state}
              </span>
            </div>
          )}

          {/* Validated citations */}
          {result.citations && result.citations.length > 0 && (
            <div className="space-y-2" data-testid="citations-section">
              <p className="text-xs font-mono text-gray-400 uppercase tracking-wide">
                Validated citations ({result.citations.length})
              </p>
              {result.citations.map((cit, i) => (
                <CitationCard
                  key={cit.passage_id + i}
                  citation={cit}
                  idx={i}
                  contextItems={result.context_items}
                />
              ))}
            </div>
          )}

          {/* Limitations */}
          {result.limitations && result.limitations.length > 0 && (
            <div className="space-y-1" data-testid="limitations-section">
              <p className="text-xs font-mono text-gray-400 uppercase tracking-wide">
                Limitations
              </p>
              <ul className="text-xs text-gray-600 space-y-0.5 list-disc list-inside">
                {result.limitations.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          )}

          {/* Context packet summary */}
          <ContextSummaryPanel
            contextItems={result.context_items}
            contextFingerprint={result.context_fingerprint}
            contextTruncated={result.context_truncated}
          />

          {/* Evidence hierarchy note */}
          {result.evidence_hierarchy_note && (
            <div
              data-testid="live-evidence-hierarchy-note"
              className="text-xs text-gray-500 border border-gray-100 rounded p-3"
            >
              <strong className="text-gray-600">Evidence hierarchy: </strong>
              {result.evidence_hierarchy_note}
            </div>
          )}

          {/* Supplemental deterministic results (if present and live result exists) */}
          {result.deterministic_results && result.deterministic_results.length > 0 && (
            <div className="space-y-2" data-testid="deterministic-supplement">
              <p className="text-xs font-mono text-gray-400 uppercase tracking-wide">
                Supplemental deterministic results ({result.deterministic_results.length})
              </p>
              <p className="text-xs text-gray-400 italic">
                Keyword-overlap results from the local deterministic index, provided for
                reference. These are NOT the model's primary source.
              </p>
              {result.deterministic_results.slice(0, 3).map((r, i) => (
                <ResultCard
                  key={r.passage_id || r.claim_id || r.constraint_id || i}
                  result={r}
                  idx={i}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Query history */}
      <GovernedQueryHistoryPanel
        history={history}
        loading={historyLoading}
        show={showHistory}
        onToggle={handleHistoryToggle}
      />
    </div>
  );
}



export default function Brain() {
  const [tab, setTab] = useState('findings');

  const TABS = [
    { id: 'findings', label: 'Findings' },
    { id: 'query',    label: 'Governed Query' },
    { id: 'live',     label: 'Live Intelligence' },
  ];

  return (
    <div data-testid="brain-page">
      <h1 className="page-heading mb-1">Brain</h1>
      <p className="font-body text-gray-500 mb-6" style={{ fontSize: '15px' }}>
        Read-only governed retrieval. Findings from version comparison. Queries search
        canon-locked passages and reviewed candidate records only.
      </p>

      {/* Tab bar */}
      <div className="flex gap-0 mb-6 border-b" style={{ borderColor: '#e5e5e5' }}>
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className="font-heading font-semibold px-4 py-2 transition-colors"
            style={{
              fontSize: '13px',
              color: tab === t.id ? '#000' : '#a3a3a3',
              borderBottom: tab === t.id ? '2px solid #000' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'findings' && <FindingsTab />}
      {tab === 'query'    && <GovernedQueryTab />}
      {tab === 'live'     && <LiveGovernedTab />}
    </div>
  );
}
